import { useEffect, useState } from 'react';

import { Button, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useSuaBo, useTaoBo } from '../hooks/useThamDinh';
import type { BoTieuChi, LoaiHoSo } from '../types';

export interface FormBoTieuChiProps {
  open: boolean;
  onClose: () => void;
  loaiHoSo: LoaiHoSo[];
  dangSua?: Pick<BoTieuChi, 'id' | 'loai_ho_so_id' | 'ten' | 'mo_ta'>;
}

/**
 * Tạo hoặc sửa phần **vỏ** của một bộ tiêu chí: tên, mô tả, loại hồ sơ.
 *
 * Cây tiêu chí (nhóm, tiêu chí, trọng số) soạn ở màn riêng của đợt sau — backend đã có
 * `PUT /bo-tieu-chi/{id}/ban-nhap` với khoá lạc quan, nhưng trình soạn cây là việc của
 * ADR-0080 đợt 2. Bộ tạo ở đây là nháp rỗng, đúng như đặc tả mục 13.3 mô tả.
 */
export function FormBoTieuChi({ open, onClose, loaiHoSo, dangSua }: FormBoTieuChiProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const tao = useTaoBo();
  const sua = useSuaBo();
  const chay = dangSua ? sua : tao;

  const [loaiId, setLoaiId] = useState<string | undefined>();
  const [ten, setTen] = useState('');
  const [moTa, setMoTa] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoaiId(dangSua?.loai_ho_so_id);
    setTen(dangSua?.ten ?? '');
    setMoTa(dangSua?.mo_ta ?? '');
    chay.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dangSua]);

  const loiTruong = loiTheoTruong(chay.error);
  const loiChung = chay.error && Object.keys(loiTruong).length === 0 ? dichLoi(chay.error) : undefined;

  function luu() {
    if (dangSua) {
      sua.mutate({ id: dangSua.id, du: { ten, mo_ta: moTa } }, { onSuccess: onClose });
    } else if (loaiId) {
      tao.mutate({ loai_ho_so_id: loaiId, ten, mo_ta: moTa }, { onSuccess: onClose });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(dangSua ? 'thamDinh.bo.formSuaTieuDe' : 'thamDinh.bo.formTaoTieuDe')}
    >
      <div className="space-y-4 pt-2">
        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        <Field
          label={t('thamDinh.bo.nhanLoaiHoSo')}
          required={!dangSua}
          hint={t('thamDinh.bo.moLoaiHoSo')}
          error={loiTruong['/loai_ho_so_id']}
        >
          {(p) => (
            <Select
              {...p}
              className="w-full"
              value={loaiId}
              disabled={Boolean(dangSua)}
              placeholder={t('thamDinh.bo.chonLoaiHoSo')}
              options={loaiHoSo.map((l) => ({ value: l.id, label: l.ten }))}
              onChange={(v) => setLoaiId(v)}
            />
          )}
        </Field>

        <Field label={t('thamDinh.bo.nhanTen')} required error={loiTruong['/ten']}>
          {(p) => <Input {...p} value={ten} onChange={(e) => setTen(e.target.value)} />}
        </Field>

        <Field label={t('thamDinh.bo.nhanMoTa')} error={loiTruong['/mo_ta']}>
          {(p) => <Textarea {...p} rows={3} value={moTa} onChange={(e) => setMoTa(e.target.value)} />}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={chay.isPending}
            disabled={!ten || (!dangSua && !loaiId)}
            onClick={luu}
          >
            {t('chung.luu')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
